/**
 * API が返すエラーコード。
 *
 * `as const` 配列を単一の情報源にして、型と実行時の値がズレないようにする。
 */
export const API_ERROR_CODES = [
    'VALIDATION_ERROR',
    'UNAUTHORIZED',
    'FORBIDDEN',
    'NOT_FOUND',
    'CONFLICT',
    'RATE_LIMIT_EXCEEDED',
    'INTERNAL_ERROR',
    // AI 鑑定（docs/specs/ai-appraisal.md）
    'DRAWING_NOT_RECOGNIZED',
    'DRAWING_INAPPROPRIATE',
    /**
     * 絵の中に、個人が分かる文字（名字と名前・学校・クラスなど）が書かれている（422、#259）。**描いた絵は消さない**
     * （その文字だけ消して送り直してもらう）。問題のある絵（`DRAWING_INAPPROPRIATE`）とは断り方を分ける。
     */
    'DRAWING_PERSONAL_INFO',
    'APPRAISAL_UNAVAILABLE',
    // 引き継ぎ（#119、ADR-022）
    'LINK_TOKEN_INVALID',
    'ALREADY_LINKED',
    'GUEST_HAS_DATA',
    'ACCOUNT_NOT_FOUND',
    'ACCOUNT_LINK_UNAVAILABLE',
    /**
     * 管理者がこのアカウントを利用停止にした（403、#317、ADR-034）。**認証の要る API が、今のセッションの持ち主について返す。**
     * `UNAUTHORIZED` と分けてある。アプリは 401 ならトークンを捨てて「はじめかた」に戻すので、混ぜると新しいゲストを
     * 作れてしまう。アプリはトークンを残したまま停止の画面にする。理由は返さない。
     */
    'ACCOUNT_SUSPENDED',
    /**
     * 引き継ぎ（`/auth/restore`・`/auth/link`）で、**このログインに結びついたアカウント**が利用停止中（403、#317）。
     * `ACCOUNT_SUSPENDED` と分けてある。`/auth/link` は今のゲストのセッションで送るので、同じコードだと「今のゲストが
     * 停止中」と区別できず、アプリが使えるゲストまで停止の画面にしてしまう。
     */
    'LINKED_ACCOUNT_SUSPENDED',
    /**
     * AI 鑑定の説明に同意していない（403、#121）。**アプリは絵を送る前に `GET /me` で確かめる**ので、
     * これが返るのはアプリの不具合か、説明の版が上がった直後。
     */
    'CONSENT_REQUIRED',
    /**
     * 手描きのアバター（#169）を AI で確かめられなかった（503）。描いた絵は残して、あとで送り直してもらう。
     * 問題のある絵で断ったときは鑑定と同じ `DRAWING_INAPPROPRIATE`（422）。
     */
    'AVATAR_CHECK_UNAVAILABLE',
    // プライベートマッチ（#153、ADR-024）。参加できない理由ごとに分け、アプリが別々の文言を出す。
    // コードに当たるルームが無いときは `NOT_FOUND`（`details.field` が `code`）。
    /** 期限が来た（410）。ホストに新しいルームを作ってもらう。 */
    'PRIVATE_MATCH_EXPIRED',
    /** ほかの人がもう参加している（409）。 */
    'PRIVATE_MATCH_FULL',
    /** ホストが取りやめた、または対戦が済んだ（409）。 */
    'PRIVATE_MATCH_CLOSED',
    /** 自分が作ったルームに、自分でゲストとして入ろうとした（409）。 */
    'PRIVATE_MATCH_OWN_ROOM',
    /**
     * 管理者の API（`/admin`、#250）の設定（`CLERK_ISSUER` / `CLERK_JWT_KEY` / `ADMIN_ORIGIN`）が環境に無い（503）。
     * 管理画面だけが受け取る。
     */
    'ADMIN_NOT_CONFIGURED',
    /**
     * 管理者が非公開にした選手（#251、ADR-031）で、大会・ともだちと対戦に出ようとした（409）。**理由は返さない**
     * （持ち主には伝えない決定。メッセージは「この キャラは つかえません」だけ）。
     */
    'CHARACTER_UNAVAILABLE',
    /**
     * ワールドカップ（#254、ADR-032）をこの環境で開いていない（403）。本番は、UGC の安全の仕組みがそろうまで閉じておく。
     */
    'WORLD_CUP_UNAVAILABLE',
    /**
     * 選手の名前・必殺技の名前・ニックネームに、悪い言葉・性的な言葉・差別的な言葉が入っている（422、#258、ADR-032 A2）。
     * **どの種類かは返さない**（すり抜ける手がかりを与えない。ADR-021 と同じ）。`details.field` にどの入力欄か。
     */
    'NAME_INAPPROPRIATE',
    /** 名前に、個人が分かること（名字と名前・学校・クラス・電話番号・住所）が入っている（422、#258）。直し方を伝える。 */
    'NAME_PERSONAL_INFO',
    /** 名前を AI で確かめられなかった（503、#258）。**素通しにしない。** 少し待って送り直してもらう。 */
    'NAME_CHECK_UNAVAILABLE',
];
